import { supabase } from '../lib/supabase';
import type {
  CalendarEvent,
  CalendarItem,
  CreateCalendarEventInput,
  UpdateCalendarEventInput,
  RecurrenceType,
} from '../types/calendar';
import type { Todo } from '../types/todo';
import { addDays, addWeeks, addMonths, addYears, getStartOfDay, getEndOfDay } from '../types/calendar';

// =============================================
// Calendar Events CRUD
// =============================================

export async function getCalendarEvents(
  userId: string,
  startDate: Date,
  endDate: Date
): Promise<CalendarEvent[]> {
  const { data, error } = await supabase
    .from('calendar_events')
    .select('*')
    .eq('user_id', userId)
    .or(`start_at.lte.${endDate.toISOString()},end_at.gte.${startDate.toISOString()}`)
    .order('start_at', { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function getCalendarEventById(
  eventId: string,
  userId: string
): Promise<CalendarEvent | null> {
  const { data, error } = await supabase
    .from('calendar_events')
    .select('*')
    .eq('id', eventId)
    .eq('user_id', userId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function createCalendarEvent(
  userId: string,
  input: CreateCalendarEventInput
): Promise<CalendarEvent> {
  const { data, error } = await supabase
    .from('calendar_events')
    .insert({
      ...input,
      user_id: userId,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateCalendarEvent(
  eventId: string,
  userId: string,
  input: UpdateCalendarEventInput
): Promise<CalendarEvent> {
  const { data, error } = await supabase
    .from('calendar_events')
    .update({
      ...input,
      updated_at: new Date().toISOString(),
    })
    .eq('id', eventId)
    .eq('user_id', userId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteCalendarEvent(
  eventId: string,
  userId: string
): Promise<void> {
  const { error } = await supabase
    .from('calendar_events')
    .delete()
    .eq('id', eventId)
    .eq('user_id', userId);

  if (error) throw error;
}

// =============================================
// Get Todos for Calendar
// =============================================

export async function getTodosForCalendar(
  userId: string,
  startDate: Date,
  endDate: Date
): Promise<Todo[]> {
  const startStr = startDate.toISOString().split('T')[0];
  const endStr = endDate.toISOString().split('T')[0];

  const { data, error } = await supabase
    .from('todos')
    .select('*')
    .eq('user_id', userId)
    .gte('due_date', startStr)
    .lte('due_date', endStr)
    .order('due_date', { ascending: true });

  if (error) throw error;
  return data || [];
}

// =============================================
// Recurrence Expansion
// =============================================

function expandRecurringEvent(
  event: CalendarEvent,
  rangeStart: Date,
  rangeEnd: Date
): CalendarItem[] {
  const items: CalendarItem[] = [];
  const eventStart = new Date(event.start_at);
  const eventEnd = new Date(event.end_at);
  const duration = eventEnd.getTime() - eventStart.getTime();

  // If no recurrence, just return single item if in range
  if (event.recurrence === 'none') {
    if (eventStart <= rangeEnd && eventEnd >= rangeStart) {
      items.push(eventToCalendarItem(event, eventStart, eventEnd));
    }
    return items;
  }

  // Calculate recurrence end
  let recurrenceEnd = rangeEnd;
  if (event.recurrence_until) {
    const untilDate = new Date(event.recurrence_until);
    if (untilDate < recurrenceEnd) {
      recurrenceEnd = untilDate;
    }
  }

  let occurrenceCount = 0;
  const maxOccurrences = event.recurrence_count || 365; // Safety limit
  let currentStart = new Date(eventStart);

  // Find first occurrence in or after range start
  while (currentStart < rangeStart && occurrenceCount < maxOccurrences) {
    currentStart = getNextOccurrence(currentStart, event.recurrence, event.recurrence_interval || 1, event.recurrence_days_of_week);
    occurrenceCount++;
  }

  // Generate occurrences within range
  while (currentStart <= recurrenceEnd && occurrenceCount < maxOccurrences) {
    const currentEnd = new Date(currentStart.getTime() + duration);

    // For weekly recurrence with specific days, check if current day matches
    if (event.recurrence === 'weekly' && event.recurrence_days_of_week && event.recurrence_days_of_week.length > 0) {
      if (event.recurrence_days_of_week.includes(currentStart.getDay())) {
        items.push(eventToCalendarItem(event, currentStart, currentEnd, true));
      }
    } else {
      items.push(eventToCalendarItem(event, currentStart, currentEnd, true));
    }

    currentStart = getNextOccurrence(currentStart, event.recurrence, event.recurrence_interval || 1, event.recurrence_days_of_week);
    occurrenceCount++;

    if (event.recurrence_count && occurrenceCount >= event.recurrence_count) {
      break;
    }
  }

  return items;
}

function getNextOccurrence(
  date: Date,
  recurrence: RecurrenceType,
  interval: number,
  daysOfWeek: number[] | null
): Date {
  switch (recurrence) {
    case 'daily':
      return addDays(date, interval);
    case 'weekly':
      if (daysOfWeek && daysOfWeek.length > 0) {
        // Find next day in the list
        let nextDate = addDays(date, 1);
        let weeksChecked = 0;
        while (weeksChecked < 8) { // Safety limit
          if (daysOfWeek.includes(nextDate.getDay())) {
            return nextDate;
          }
          nextDate = addDays(nextDate, 1);
          if (nextDate.getDay() === date.getDay()) {
            weeksChecked++;
            // Skip ahead by interval-1 weeks if we completed a week cycle
            if (interval > 1) {
              nextDate = addWeeks(nextDate, interval - 1);
            }
          }
        }
        return addWeeks(date, interval);
      }
      return addWeeks(date, interval);
    case 'monthly':
      return addMonths(date, interval);
    case 'yearly':
      return addYears(date, interval);
    default:
      return addDays(date, 1);
  }
}

function eventToCalendarItem(
  event: CalendarEvent,
  start: Date,
  end: Date,
  isRecurring: boolean = false
): CalendarItem {
  return {
    id: isRecurring ? `${event.id}_${start.toISOString()}` : event.id,
    source: 'event',
    title: event.title,
    start,
    end,
    allDay: event.all_day,
    eventType: event.type,
    description: event.description,
    location: event.location,
    isRecurring,
    originalEventId: event.id,
  };
}

function todoToCalendarItem(todo: Todo): CalendarItem {
  const dueDate = new Date(todo.due_date);
  return {
    id: todo.id,
    source: 'todo',
    title: todo.title,
    start: getStartOfDay(dueDate),
    end: getEndOfDay(dueDate),
    allDay: true,
    todoCompleted: todo.completed,
    todoTag: todo.tag,
    todoSourceType: todo.source_type,
    todoSourceId: todo.source_id,
  };
}

// =============================================
// Combined Calendar Items
// =============================================

export async function getCalendarItems(
  userId: string,
  startDate: Date,
  endDate: Date
): Promise<CalendarItem[]> {
  // Fetch events and todos in parallel
  const [events, todos] = await Promise.all([
    getCalendarEvents(userId, startDate, endDate),
    getTodosForCalendar(userId, startDate, endDate),
  ]);

  const items: CalendarItem[] = [];

  // Expand recurring events and add to items
  for (const event of events) {
    const expandedItems = expandRecurringEvent(event, startDate, endDate);
    items.push(...expandedItems);
  }

  // Convert todos to calendar items
  for (const todo of todos) {
    items.push(todoToCalendarItem(todo));
  }

  // Sort by start date
  items.sort((a, b) => a.start.getTime() - b.start.getTime());

  return items;
}

// =============================================
// Utility Functions
// =============================================

export function getItemsForDay(items: CalendarItem[], date: Date): CalendarItem[] {
  const dayStart = getStartOfDay(date);
  const dayEnd = getEndOfDay(date);

  return items.filter((item) => {
    // Item overlaps with this day
    return item.start <= dayEnd && item.end >= dayStart;
  });
}

export function isMultiDayItem(item: CalendarItem): boolean {
  const startDay = getStartOfDay(item.start);
  const endDay = getStartOfDay(item.end);
  return startDay.getTime() !== endDay.getTime();
}
